import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Image,
  Modal,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { contactStore } from '../services/contactService';
import { Order, OrderItem, Product, Store } from '../lib/supabase';
import { orderService } from '../services/orderService';
import { COLORS, SPACING, FONT_SIZE, RADIUS } from '../config/theme';
import { errorHandler, ErrorCategory, ErrorSeverity } from '../utils/errorHandler';
import { RootStackParamList } from '../navigation/types';
import { Card, LoadingSpinner, OrderTimeline } from '../components';
import { cloudinaryService } from '../services/cloudinaryService';
import { refundService } from '../services/refundService';
import { returnService } from '../services/returnService';

type RouteProps = RouteProp<RootStackParamList, 'ClientOrderDetail'>;
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface OrderWithDetails extends Order {
  store?: Store;
  users?: any;
  order_items: (OrderItem & { product?: Product })[];
}

export const ClientOrderDetailScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { orderId } = route.params;
  
  const [order, setOrder] = useState<OrderWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refunds, setRefunds] = useState<any[]>([]);
  const [returns, setReturns] = useState<any[]>([]);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [showConfirmReceptionModal, setShowConfirmReceptionModal] = useState(false);
  const [showSuccessReceptionModal, setShowSuccessReceptionModal] = useState(false);

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      // Charger les vraies données de la commande avec infos utilisateur et boutique
      const orderData = await orderService.getById(orderId, { includeUser: true, includeStore: true });
      
      if (!orderData) {
        setOrder(null);
        return;
      }
      
      // Normaliser: Supabase peut retourner `products` ou `product`, normaliser vers `product`
      const orderAny = orderData as any;
      const normalized = {
        ...orderAny,
        order_items: ((orderAny.order_items || []) as any[]).map((item: any) => ({
          ...item,
          product: item.product || item.products, // Normaliser le nom de la clé
        })),
      };
      
      setOrder(normalized as OrderWithDetails);

      // Charger les remboursements de cette commande
      try {
        const refundList = await refundService.getRefundsByOrder(orderId);
        setRefunds(refundList || []);
      } catch (err) {
        console.warn('Error loading refunds:', err);
      }

      // Charger les retours de cette commande
      try {
        const returnList = await returnService.getUserReturns(order!.user_id || '');
        const orderReturns = returnList.filter((r: any) => r.order_id === orderId);
        setReturns(orderReturns || []);
      } catch (err) {
        console.warn('Error loading returns:', err);
      }
    } catch (error) {
      errorHandler.handleDatabaseError(error, 'Error loading order:');
      Alert.alert('Erreur', 'Impossible de charger la commande');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateReturn = async () => {
    if (!returnReason.trim()) {
      Alert.alert('Erreur', 'Veuillez saisir un motif pour votre retour');
      return;
    }

    setSubmittingReturn(true);
    try {
      await refundService.createRefund({
        orderId: order!.id,
        amount: order!.total_amount,
        reason: returnReason,
        type: 'full',
        status: 'pending',
        items: order!.order_items.map(item => ({
          productId: item.product_id,
          productName: item.product?.name || 'Produit',
          quantity: item.quantity,
          price: item.price,
          refundAmount: item.price * item.quantity,
        }))
      });

      Alert.alert('Succès ✓', 'Votre demande de retour a été soumise avec succès.');
      setShowReturnModal(false);
      setReturnReason('');
      loadOrder();
    } catch (err) {
      errorHandler.handle(err, 'Create refund error', ErrorCategory.USER_INPUT, ErrorSeverity.MEDIUM);
      Alert.alert('Erreur', 'Impossible d’initier le retour de commande.');
    } finally {
      setSubmittingReturn(false);
    }
  };

  const getStatusColor = (status: Order['status']): string => {
    switch (status) {
      case 'pending': return COLORS.warning;
      case 'paid': return COLORS.accent;
      case 'shipped': return COLORS.accent2;
      case 'delivered': return COLORS.success;
      case 'cancelled': return COLORS.danger;
      default: return COLORS.textMuted;
    }
  };

  const getStatusLabel = (status: Order['status']): string => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'paid': return 'Payée';
      case 'shipped': return 'Expédiée';
      case 'delivered': return 'Livrée';
      case 'cancelled': return 'Annulée';
      default: return status;
    }
  };

  const getPaymentMethodLabel = (method: string): string => {
    switch (method) {
      case 'mobile_money': return 'Mobile Money';
      case 'card': return 'Carte bancaire';
      case 'cash_on_delivery': return 'Paiement à la livraison';
      default: return method;
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!order) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Commande non trouvée</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={COLORS.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Détails de la commande</Text>
        <View style={{ width: 40 }} />
      </View>

      {/* Order ID & Status */}
      <View style={styles.orderInfo}>
        <Text style={styles.orderId}>Commande #{order.id.slice(0, 8)}</Text>
        <Text style={styles.orderDate}>{formatDate(order.created_at)}</Text>
      </View>

      {/* Return Banner */}
      {returns.length > 0 && (
        <View style={[styles.section, { borderColor: '#FF6B6B', borderWidth: 1, backgroundColor: '#FF6B6B10' }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Ionicons name="return-down-back" size={24} color="#FF6B6B" />
            <View style={{ marginLeft: SPACING.sm, flex: 1 }}>
              <Text style={[styles.sectionTitle, { color: '#FF6B6B', marginBottom: 4 }]}>Commande avec retour(s)</Text>
              <Text style={{ color: COLORS.textSoft, fontSize: FONT_SIZE.sm }}>
                {returns.length} produit(s) retourné(s) sur cette commande
              </Text>
            </View>
          </View>
        </View>
      )}

      {/* Order Timeline */}
      <OrderTimeline status={order.status} />

      {/* Store Info */}
      {order.store && (
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Boutique</Text>
          <TouchableOpacity style={styles.storeInfo}>
            <View style={styles.storeIcon}>
              <Ionicons name="storefront-outline" size={20} color={COLORS.accent} />
            </View>
            <View style={styles.storeDetails}>
              <Text style={styles.storeName}>{order.store.name}</Text>
              <Text style={styles.storeCategory}>{order.store.category}</Text>
            </View>
            <Ionicons name="chevron-forward" size={20} color={COLORS.textMuted} />
          </TouchableOpacity>
        </Card>
      )}

      {/* Client Info */}
      {order.users && (
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Client</Text>
          <View style={styles.infoRow}>
            <Ionicons name="person-outline" size={18} color={COLORS.textMuted} />
            <Text style={styles.infoText}>{order.users.full_name || 'N/A'}</Text>
          </View>
          {order.users.email && (
            <View style={styles.infoRow}>
              <Ionicons name="mail-outline" size={18} color={COLORS.textMuted} />
              <Text style={styles.infoText}>{order.users.email}</Text>
            </View>
          )}
          {order.users.phone && (
            <View style={styles.infoRow}>
              <Ionicons name="call-outline" size={18} color={COLORS.textMuted} />
              <Text style={styles.infoText}>{order.users.phone}</Text>
            </View>
          )}
        </Card>
      )}

      {/* Delivery Info */}
      <Card style={styles.section}>
        <Text style={styles.sectionTitle}>Livraison</Text>
        <View style={styles.infoRow}>
          <Ionicons name="call-outline" size={18} color={COLORS.textMuted} />
          <Text style={styles.infoText}>{order.customer_phone}</Text>
        </View>
        {order.shipping_address && (
          <View style={styles.infoRow}>
            <Ionicons name="location-outline" size={18} color={COLORS.textMuted} />
            <Text style={styles.infoText}>{order.shipping_address}</Text>
          </View>
        )}
        {order.notes && (
          <View style={styles.infoRow}>
            <Ionicons name="document-text-outline" size={18} color={COLORS.textMuted} />
            <Text style={styles.infoText}>{order.notes}</Text>
          </View>
        )}
      </Card>

      {/* Tracking Info (Client view) */}
      {order.tracking_number && (
        <Card style={[styles.section, { borderColor: COLORS.accent, borderWidth: 1 }]}>
          <Text style={[styles.sectionTitle, { color: COLORS.accent }]}>Suivi de livraison</Text>
          <View style={styles.infoRow}>
            <Ionicons name="barcode-outline" size={18} color={COLORS.textMuted} />
            <Text style={styles.infoText}>{order.tracking_number}</Text>
          </View>
          {order.shipping_provider && (
            <View style={styles.infoRow}>
              <Ionicons name="cube-outline" size={18} color={COLORS.textMuted} />
              <Text style={styles.infoText}>{order.shipping_provider}</Text>
            </View>
          )}
          {order.estimated_delivery_date && (
            <View style={styles.infoRow}>
              <Ionicons name="calendar-outline" size={18} color={COLORS.textMuted} />
              <Text style={styles.infoText}>
                Livraison estimée: {new Date(order.estimated_delivery_date).toLocaleDateString('fr-FR')}
              </Text>
            </View>
          )}
        </Card>
      )}

      {/* Out of stock warning */}
      {order.issue_type === 'out_of_stock' && (
        <Card style={[styles.section, { borderColor: COLORS.danger, borderWidth: 1, backgroundColor: COLORS.danger + '10' }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: SPACING.sm }}>
            <Ionicons name="warning" size={24} color={COLORS.danger} />
            <Text style={[styles.sectionTitle, { color: COLORS.danger, marginLeft: SPACING.sm, marginBottom: 0 }]}>Rupture de stock</Text>
          </View>
          <Text style={{ color: COLORS.danger, marginBottom: SPACING.md }}>
            Certains produits de votre commande sont actuellement en rupture de stock. Le vendeur ne peut pas accepter la commande en l'état.
          </Text>

          {/* Liste des produits en rupture et date prévue */}
          {order.issue_details && order.issue_details.length > 0 && (
            <View style={{ marginBottom: SPACING.md, backgroundColor: '#FFF', padding: SPACING.sm, borderRadius: RADIUS.sm, borderWidth: 1, borderColor: COLORS.danger + '30' }}>
              <Text style={{ fontWeight: '600', color: COLORS.text, marginBottom: SPACING.xs }}>Produits concernés :</Text>
              {order.issue_details.map((item) => (
                <View key={item.product_id} style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                  <Text style={{ color: COLORS.text, flex: 1 }} numberOfLines={1}>• {item.name}</Text>
                  <Text style={{ color: COLORS.textMuted, fontSize: FONT_SIZE.sm }}>
                    Prévu : {item.restock_date || 'Non définie'}
                  </Text>
                </View>
              ))}
            </View>
          )}

          <View style={{ gap: SPACING.sm }}>
            <TouchableOpacity 
              style={[styles.helpButton, { backgroundColor: COLORS.danger, borderColor: COLORS.danger }]}
              onPress={() => {
                Alert.alert('Annuler la commande', 'Êtes-vous sûr de vouloir annuler toute la commande ?', [
                  { text: 'Non', style: 'cancel' },
                  { 
                    text: 'Oui, annuler', 
                    style: 'destructive',
                    onPress: async () => {
                      setLoading(true);
                      try {
                        await orderService.respondToStockIssue(order.id, 'cancel');
                        Alert.alert('Commande annulée', 'Votre commande a été annulée. Le vendeur a été informé.');
                        loadOrder();
                      } catch (e) {
                        Alert.alert('Erreur', 'Impossible d\'annuler la commande');
                      } finally {
                        setLoading(false);
                      }
                    }
                  }
                ]);
              }}
            >
              <Ionicons name="close-circle" size={20} color="#FFF" />
              <Text style={[styles.helpText, { color: '#FFF' }]}>Annuler la commande</Text>
            </TouchableOpacity>

            {order.order_items.length > (order.issue_details?.length || 0) && (
              <TouchableOpacity 
                style={[styles.helpButton, { borderColor: COLORS.accent, backgroundColor: COLORS.accent + '10' }]}
                onPress={() => {
                  const issueDetails = order.issue_details || [];
                  const remainingItems = order.order_items.filter(
                    (item) => !issueDetails.find((d: any) => d.product_id === item.product_id)
                  );
                  const remainingNames = remainingItems.map(i => `• ${i.product?.name || 'Produit'}`).join('\n');
                  Alert.alert(
                    'Continuer sans les produits en rupture', 
                    `Votre commande sera modifiée pour ne contenir que :\n\n${remainingNames}\n\nLe vendeur sera informé.`, 
                    [
                      { text: 'Annuler', style: 'cancel' },
                      { 
                        text: 'Oui, continuer', 
                        onPress: async () => {
                          setLoading(true);
                          try {
                            await orderService.respondToStockIssue(order.id, 'continue_without');
                            Alert.alert('Choix enregistré', 'Le vendeur a été informé. Il ajustera votre commande.');
                            loadOrder();
                          } catch (e) {
                            Alert.alert('Erreur', 'Impossible de traiter votre demande');
                          } finally {
                            setLoading(false);
                          }
                        }
                      }
                    ]
                  );
                }}
              >
                <Ionicons name="arrow-forward-circle" size={20} color={COLORS.accent} />
                <Text style={[styles.helpText, { color: COLORS.accent }]}>Continuer avec les autres produits</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity 
              style={[styles.helpButton, { borderColor: COLORS.warning, backgroundColor: COLORS.warning + '10' }]}
              onPress={() => {
                Alert.alert(
                  'Attendre la disponibilité',
                  'Vous serez notifié dès que le vendeur aura réapprovisionné les produits. Confirmer ?',
                  [
                    { text: 'Annuler', style: 'cancel' },
                    {
                      text: 'Oui, attendre',
                      onPress: async () => {
                        setLoading(true);
                        try {
                          await orderService.respondToStockIssue(order.id, 'wait');
                          Alert.alert('Choix enregistré', 'Le vendeur a été informé. Vous serez notifié dès que le stock sera disponible.');
                          loadOrder();
                        } catch (e) {
                          Alert.alert('Erreur', 'Impossible de traiter votre demande');
                        } finally {
                          setLoading(false);
                        }
                      }
                    }
                  ]
                );
              }}
            >
              <Ionicons name="time" size={20} color={COLORS.warning} />
              <Text style={[styles.helpText, { color: COLORS.warning }]}>Attendre la disponibilité</Text>
            </TouchableOpacity>
          </View>
        </Card>
      )}

      {/* Waiting for restock banner */}
      {order.issue_type === 'waiting_restock' && (
        <Card style={[styles.section, { borderColor: COLORS.warning, borderWidth: 1, backgroundColor: COLORS.warning + '10' }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: SPACING.sm }}>
            <Ionicons name="time" size={24} color={COLORS.warning} />
            <Text style={[styles.sectionTitle, { color: COLORS.warning, marginLeft: SPACING.sm, marginBottom: 0 }]}>En attente de réapprovisionnement</Text>
          </View>
          <Text style={{ color: COLORS.textSoft }}>
            Vous avez choisi d'attendre que le vendeur réapprovisionne les produits en rupture. Vous serez notifié dès que la commande pourra être traitée.
          </Text>
          {order.issue_details && order.issue_details.length > 0 && (
            <View style={{ marginTop: SPACING.sm }}>
              {order.issue_details.map((item) => (
                <View key={item.product_id} style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                  <Text style={{ color: COLORS.text, flex: 1 }} numberOfLines={1}>• {item.name}</Text>
                  <Text style={{ color: COLORS.warning, fontSize: FONT_SIZE.sm, fontWeight: '600' }}>
                    {item.restock_date || 'Date non définie'}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </Card>
      )}

      {/* Resolved partial banner */}
      {order.issue_type === 'resolved_partial' && (
        <Card style={[styles.section, { borderColor: COLORS.accent, borderWidth: 1, backgroundColor: COLORS.accent + '10' }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: SPACING.sm }}>
            <Ionicons name="swap-horizontal" size={24} color={COLORS.accent} />
            <Text style={[styles.sectionTitle, { color: COLORS.accent, marginLeft: SPACING.sm, marginBottom: 0 }]}>Commande modifiée</Text>
          </View>
          <Text style={{ color: COLORS.textSoft }}>
            Vous avez choisi de continuer sans les produits en rupture. Le vendeur va ajuster votre commande.
          </Text>
        </Card>
      )}

      {/* Products */}
      <Card style={styles.section}>
        <Text style={styles.sectionTitle}>Produits ({order.order_items.length})</Text>
        {order.order_items.map((item) => {
          const productImage = item.product?.images && item.product.images.length > 0
            ? item.product.images[0]
            : null;

          const itemReturn = returns.find((r: any) => r.product_id === item.product_id);

          return (
            <View
              key={item.id}
              style={[
                styles.itemContainer,
                itemReturn && {
                  borderWidth: 2,
                  borderColor: '#FF6B6B',
                  backgroundColor: '#FF6B6B05',
                }
              ]}
            >
              <View style={styles.itemImage}>
                {productImage ? (
                  <Image
                    source={{ uri: cloudinaryService.getOptimizedUrl(productImage, 300) }}
                    style={{ width: '100%', height: '100%', borderRadius: 8 }}
                  />
                ) : (
                  <View style={{ width: '100%', height: '100%', borderRadius: 8, backgroundColor: COLORS.border, alignItems: 'center', justifyContent: 'center' }}>
                    <Ionicons name="cube-outline" size={24} color={COLORS.textMuted} />
                  </View>
                )}
              </View>
              <View style={styles.itemInfo}>
                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Text style={styles.itemName}>{item.product?.name || 'Produit inconnu'}</Text>
                  {itemReturn && (
                    <View style={{ backgroundColor: '#FF6B6B', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 10, marginLeft: 8 }}>
                      <Text style={{ color: '#fff', fontSize: 10, fontWeight: 'bold' }}>RETOURNÉ</Text>
                    </View>
                  )}
                </View>
                <Text style={styles.itemPrice}>
                  {item.price.toLocaleString()} FCFAs × {item.quantity}
                </Text>
              </View>
              <Text style={styles.itemTotal}>
                {(item.price * item.quantity).toLocaleString()} FCFAs
              </Text>
            </View>
          );
        })}
      </Card>

      {/* Payment Info */}
      <Card style={styles.section}>
        <Text style={styles.sectionTitle}>Paiement</Text>
        <View style={styles.infoRow}>
          <Ionicons name="card-outline" size={18} color={COLORS.textMuted} />
          <Text style={styles.infoText}>{getPaymentMethodLabel(order.payment_method)}</Text>
        </View>
        <View style={styles.infoRow}>
          <Ionicons 
            name={order.payment_status === 'paid' ? 'checkmark-circle' : 'time-outline'} 
            size={18} 
            color={order.payment_status === 'paid' ? COLORS.success : COLORS.warning} 
          />
          <Text style={[
            styles.infoText,
            { color: order.payment_status === 'paid' ? COLORS.success : COLORS.warning }
          ]}>
            {order.payment_status === 'paid' ? 'Payé' : 'En attente'}
          </Text>
        </View>
      </Card>

      {/* Total */}
      <Card style={styles.section}>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Total</Text>
          <Text style={styles.totalValue}>
            {order.total_amount.toLocaleString()} FCFAs
          </Text>
        </View>
      </Card>

      {/* Return Requests Status */}
      {refunds.length > 0 && (
        <Card style={[styles.section, { borderColor: COLORS.accent, borderWidth: 1 }]}>
          <Text style={[styles.sectionTitle, { color: COLORS.accent }]}>Demande de retour active</Text>
          {refunds.map((ref) => (
            <View key={ref.id} style={{ marginTop: SPACING.sm }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={{ fontWeight: '600', color: COLORS.text, flex: 1, marginRight: SPACING.sm }}>Motif: {ref.reason}</Text>
                <View style={{
                  backgroundColor: ref.status === 'approved' || ref.status === 'processed' ? COLORS.success + '20' : ref.status === 'rejected' ? COLORS.danger + '20' : COLORS.warning + '20',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 4
                }}>
                  <Text style={{
                    color: ref.status === 'approved' || ref.status === 'processed' ? COLORS.success : ref.status === 'rejected' ? COLORS.danger : COLORS.warning,
                    fontSize: FONT_SIZE.xs,
                    fontWeight: '600'
                  }}>
                    {ref.status === 'pending' ? 'En attente' : ref.status === 'approved' || ref.status === 'processed' ? 'Accepté' : 'Refusé'}
                  </Text>
                </View>
              </View>
              <Text style={{ fontSize: FONT_SIZE.sm, color: COLORS.textMuted, marginTop: 4 }}>
                Créé le {new Date(ref.orderDate).toLocaleDateString('fr-FR')} — Montant: {ref.amount.toLocaleString()} FCFAs
              </Text>
            </View>
          ))}
        </Card>
      )}

      <View style={[styles.actions, { flexDirection: 'column', gap: SPACING.md }]}>
        <TouchableOpacity
          style={styles.helpButton}
          onPress={() => {
            const phone = (order.store as any)?.whatsapp_number || order.store?.phone || order.customer_phone;
            if (!phone) {
              Alert.alert('Erreur', 'Numéro de téléphone de la boutique non disponible');
              return;
            }
            contactStore({ rawPhone: phone, message: `Bonjour, je vous contacte concernant ma commande #${orderId.slice(0, 8)}` });
          }}
        >
          <Ionicons name="chatbubble-outline" size={20} color={COLORS.accent} />
          <Text style={styles.helpText}>Contacter la boutique</Text>
        </TouchableOpacity>

        {order.status === 'shipped' && (
          <TouchableOpacity
            style={[styles.helpButton, { borderColor: COLORS.success, backgroundColor: COLORS.success + '10' }]}
            onPress={() => setShowConfirmReceptionModal(true)}
          >
            <Ionicons name="checkmark-circle-outline" size={20} color={COLORS.success} />
            <Text style={[styles.helpText, { color: COLORS.success }]}>Confirmer la réception</Text>
          </TouchableOpacity>
        )}

        {order.status === 'delivered' && refunds.length === 0 && (
          <TouchableOpacity
            style={[styles.helpButton, { borderColor: COLORS.danger, backgroundColor: COLORS.danger + '10' }]}
            onPress={() => setShowReturnModal(true)}
          >
            <Ionicons name="return-up-back" size={20} color={COLORS.danger} />
            <Text style={[styles.helpText, { color: COLORS.danger }]}>Demander un retour / remboursement</Text>
          </TouchableOpacity>
        )}

        {order.status === 'delivered' && (
          <TouchableOpacity
            style={[styles.helpButton, { borderColor: COLORS.accent, backgroundColor: COLORS.accent + '10' }]}
            onPress={() => navigation.navigate('Review', {
              orderId: order.id,
              storeId: order.store_id || '',
              storeName: (order.stores as any)?.name || 'Boutique',
            })}
          >
            <Ionicons name="star-outline" size={20} color={COLORS.accent} />
            <Text style={[styles.helpText, { color: COLORS.accent }]}>Noter la commande</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Confirm Reception Modal */}
      <Modal
        visible={showConfirmReceptionModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowConfirmReceptionModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: COLORS.card, alignItems: 'center', padding: SPACING.xl }]}>
            <View style={{ width: 60, height: 60, borderRadius: 30, backgroundColor: COLORS.success + '20', alignItems: 'center', justifyContent: 'center', marginBottom: SPACING.md }}>
              <Ionicons name="checkmark-circle-outline" size={32} color={COLORS.success} />
            </View>
            <Text style={[styles.modalTitle, { color: COLORS.text, textAlign: 'center', fontSize: FONT_SIZE.lg }]}>
              Confirmer la réception
            </Text>
            <Text style={[styles.modalSubtitle, { color: COLORS.textMuted, textAlign: 'center', marginTop: SPACING.sm, marginBottom: SPACING.xl }]}>
              Avez-vous bien reçu tous les produits de cette commande ? Cette action est irréversible.
            </Text>
            
            <View style={{ flexDirection: 'row', gap: SPACING.md, width: '100%' }}>
              <TouchableOpacity
                style={[styles.modalCancelButton, { flex: 1, backgroundColor: COLORS.border + '50' }]}
                onPress={() => setShowConfirmReceptionModal(false)}
                disabled={loading}
              >
                <Text style={[styles.modalCancelText, { color: COLORS.text }]}>Non, annuler</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalConfirmButton, { flex: 1, backgroundColor: COLORS.success }]}
                onPress={async () => {
                  setShowConfirmReceptionModal(false);
                  setLoading(true);
                  try {
                    await orderService.confirmReception(order.id);
                    setShowSuccessReceptionModal(true);
                  } catch (err) {
                    Alert.alert('Erreur', 'Impossible de confirmer la réception.');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
              >
                {loading ? <ActivityIndicator color="#fff" /> : <Text style={styles.modalConfirmText}>Oui, j'ai reçu</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Success Reception Modal */}
      <Modal
        visible={showSuccessReceptionModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {}}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: COLORS.card, alignItems: 'center', padding: SPACING.xl }]}>
            <View style={{ width: 80, height: 80, borderRadius: 40, backgroundColor: COLORS.success + '20', alignItems: 'center', justifyContent: 'center', marginBottom: SPACING.lg }}>
              <Ionicons name="gift-outline" size={40} color={COLORS.success} />
            </View>
            <Text style={[styles.modalTitle, { color: COLORS.text, textAlign: 'center', fontSize: FONT_SIZE.xl }]}>
              Merci ! 🎉
            </Text>
            <Text style={[styles.modalSubtitle, { color: COLORS.textMuted, textAlign: 'center', marginTop: SPACING.sm, marginBottom: SPACING.xl, fontSize: FONT_SIZE.md }]}>
              Vous avez confirmé la réception de votre commande. Le vendeur a été notifié. Prenez un instant pour noter votre expérience !
            </Text>
            
            <TouchableOpacity
              style={[styles.modalConfirmButton, { width: '100%', backgroundColor: COLORS.accent, paddingVertical: SPACING.md }]}
              onPress={() => {
                setShowSuccessReceptionModal(false);
                loadOrder();
                navigation.navigate('Review', {
                  orderId: order.id,
                  storeId: order.store_id || '',
                  storeName: (order.stores as any)?.name || order.store?.name || 'Boutique',
                });
              }}
            >
              <Text style={[styles.modalConfirmText, { fontSize: FONT_SIZE.md }]}>Noter la boutique</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Return Request Modal */}
      <Modal
        visible={showReturnModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowReturnModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: COLORS.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: COLORS.text }]}>Demander un retour</Text>
              <TouchableOpacity onPress={() => setShowReturnModal(false)}>
                <Ionicons name="close" size={24} color={COLORS.text} />
              </TouchableOpacity>
            </View>

            <Text style={[styles.modalSubtitle, { color: COLORS.textMuted }]}>
              Veuillez expliquer en détail la raison de votre retour. Notre équipe et le vendeur l'analyseront dans les plus brefs délais.
            </Text>

            <TextInput
              style={[styles.modalInput, { height: 100, textAlignVertical: 'top' }]}
              value={returnReason}
              onChangeText={setReturnReason}
              placeholder="Raison du retour (ex: Produit défectueux, mauvaise taille...)"
              placeholderTextColor={COLORS.textMuted}
              multiline
              numberOfLines={4}
            />

            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() => setShowReturnModal(false)}
              >
                <Text style={styles.modalCancelText}>Annuler</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalConfirmButton}
                onPress={handleInitiateReturn}
                disabled={submittingReturn}
              >
                {submittingReturn ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <Text style={styles.modalConfirmText}>Soumettre</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <View style={{ height: SPACING.xxl }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.bg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACING.lg,
    paddingTop: SPACING.xxl,
    paddingBottom: SPACING.lg,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: COLORS.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: FONT_SIZE.lg,
    fontWeight: '700',
    color: COLORS.text,
  },
  orderInfo: {
    paddingHorizontal: SPACING.lg,
    marginBottom: SPACING.md,
  },
  orderId: {
    fontSize: FONT_SIZE.lg,
    fontWeight: '700',
    color: COLORS.text,
    marginBottom: SPACING.xs,
  },
  orderDate: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textMuted,
  },
  statusContainer: {
    paddingHorizontal: SPACING.lg,
    marginBottom: SPACING.lg,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: RADIUS.full,
    gap: SPACING.xs,
  },
  statusText: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
  },
  section: {
    marginHorizontal: SPACING.lg,
    marginBottom: SPACING.md,
  },
  sectionTitle: {
    fontSize: FONT_SIZE.md,
    fontWeight: '600',
    color: COLORS.text,
    marginBottom: SPACING.md,
  },
  storeInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  storeIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: COLORS.accent + '20',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: SPACING.md,
  },
  storeDetails: {
    flex: 1,
  },
  storeName: {
    fontSize: FONT_SIZE.md,
    fontWeight: '600',
    color: COLORS.text,
  },
  storeCategory: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textMuted,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: SPACING.sm,
  },
  infoText: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textSoft,
    marginLeft: SPACING.sm,
  },
  itemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.sm,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  itemImage: {
    width: 50,
    height: 50,
    borderRadius: RADIUS.md,
    backgroundColor: COLORS.bg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: SPACING.md,
  },
  itemInfo: {
    flex: 1,
  },
  itemName: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '500',
    color: COLORS.text,
  },
  itemPrice: {
    fontSize: FONT_SIZE.xs,
    color: COLORS.textMuted,
    marginTop: 2,
  },
  itemTotal: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: COLORS.text,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalLabel: {
    fontSize: FONT_SIZE.lg,
    fontWeight: '600',
    color: COLORS.text,
  },
  totalValue: {
    fontSize: FONT_SIZE.xl,
    fontWeight: '700',
    color: COLORS.accent,
  },
  actions: {
    paddingHorizontal: SPACING.lg,
    marginTop: SPACING.lg,
  },
  helpButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.full,
    borderWidth: 1,
    borderColor: COLORS.accent,
    gap: SPACING.sm,
  },
  helpText: {
    fontSize: FONT_SIZE.md,
    fontWeight: '600',
    color: COLORS.accent,
  },
  errorText: {
    fontSize: FONT_SIZE.md,
    color: COLORS.danger,
    textAlign: 'center',
    marginTop: SPACING.xxl,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    padding: SPACING.xl,
  },
  modalContent: {
    borderRadius: RADIUS.lg,
    padding: SPACING.xl,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.md,
  },
  modalTitle: {
    fontSize: FONT_SIZE.lg,
    fontWeight: '700',
  },
  modalSubtitle: {
    fontSize: FONT_SIZE.sm,
    lineHeight: 20,
    marginBottom: SPACING.lg,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: RADIUS.md,
    padding: SPACING.md,
    color: COLORS.text,
    backgroundColor: COLORS.bg,
    marginBottom: SPACING.lg,
  },
  modalFooter: {
    flexDirection: 'row',
    gap: SPACING.md,
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: SPACING.md,
    alignItems: 'center',
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  modalConfirmButton: {
    flex: 1,
    paddingVertical: SPACING.md,
    alignItems: 'center',
    borderRadius: RADIUS.md,
    backgroundColor: COLORS.danger,
  },
  modalCancelText: {
    color: COLORS.text,
    fontWeight: '600',
  },
  modalConfirmText: {
    color: '#FFF',
    fontWeight: '600',
  },
});

